import React, { useCallback, useMemo, useState } from "react";
import { useDebounceValue } from "usehooks-ts";

import { faRotateLeft } from "@fortawesome/pro-solid-svg-icons";

import { faMagnifyingGlass } from "@performyard/fontawesome/pro-regular-svg-icons";
import { ButtonIcon, InputText, Pagination, Table, Text, Tooltip } from "@performyard/py-components";
import type { ColDef, ICellRendererParams, SortChangedEvent } from "@performyard/py-components";

import { SingleEmployee } from "../../../../components/EmployeePicker";
import { SingleSelectEmployee } from "../../../../components/Inputs/SelectEmployee";

import { useSelectionManagers } from "../../../../api/hooks/defs/compensation";

type ManagerRow = {
	_id: string;
	name: string;
	jobTitle: string | null;
	directReportCount: number;
	totalCompensation: number;
	managerId: string | null;
	managerName: string | null;
};

type ManagersTableProps = {
	userSelectionId: string;
	value: Record<string, SingleEmployee>;
	onChange: (value: Record<string, SingleEmployee>) => void;
	onBlur: () => void;
};

const ManagerNameCellRenderer = (props: ICellRendererParams<ManagerRow>) => {
	const { name, jobTitle } = props.data || {};

	return (
		<div className="flex h-full flex-col justify-center gap-0.5">
			<Text size="paragraph-3" weight="regular">
				{name ?? ""}
			</Text>
			{jobTitle && (
				<Text size="caption" weight="regular" color="neutral-4">
					{jobTitle}
				</Text>
			)}
		</div>
	);
};

export const ManagersTable = ({ userSelectionId, value, onChange }: ManagersTableProps) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [debouncedSearchTerm] = useDebounceValue(searchTerm, 300);
	const [currentPage, setCurrentPage] = useState(1);
	const [sortBy, setSortBy] = useState<"name" | "directReportCount" | "totalCompensation">("name");
	const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
	const limit = 100;

	const { data, isLoading } = useSelectionManagers(userSelectionId, {
		skip: (currentPage - 1) * limit,
		limit,
		sortBy,
		sortOrder,
		search: debouncedSearchTerm || undefined,
	});

	const handleManagerOverrideChange = (managerId: string, selectedEmployee: SingleEmployee | null) => {
		const newOverrides = { ...value };

		if (selectedEmployee && selectedEmployee.value !== managerId) {
			// User selected a different manager - add to overrides
			newOverrides[managerId] = selectedEmployee;
		} else {
			// User selected the original manager or cleared - remove from overrides
			delete newOverrides[managerId];
		}

		onChange(newOverrides);
	};

	const buildFetchOptions = useCallback((row: ManagerRow) => {
		const byId = new Map<string, { value: string; label: string }>();
		byId.set(row._id, { value: row._id, label: row.name });
		if (row.managerId) {
			byId.set(row.managerId, {
				value: row.managerId,
				label: row.managerName ?? row.managerId,
			});
		}
		const sorted = [...byId.values()].sort((a, b) => a.label.localeCompare(b.label));
		return async (inputValue: string) => {
			const lower = inputValue.toLowerCase();
			return sorted.filter((c) => c.label.toLowerCase().includes(lower));
		};
	}, []);

	const columnDefs: ColDef<ManagerRow>[] = useMemo(() => {
		const ManagerOverrideCellRenderer = (props: ICellRendererParams<ManagerRow>) => {
			const managerId = props.data?._id;
			const managerName = props.data?.name;

			const row = props.data;
			if (!managerId || !managerName || !row) return null;

			// Get the current value: override if exists, otherwise create SingleEmployee for the manager themselves
			const currentSingle: SingleEmployee = value[managerId] ?? {
				label: managerName,
				value: managerId,
			};

			const fetchEmployeesForRow = async (searchTerm: string) => {
				const options = await buildFetchOptions(row)(searchTerm);
				return options.map((o) => ({ id: o.value, name: o.label }));
			};

			return (
				<div className="w-full min-w-0" id={`manager-override-${managerId}`}>
					<SingleSelectEmployee
						value={{ id: currentSingle.value, name: currentSingle.label }}
						onChange={(employeeId, employeeName) => {
							if (!employeeId || !employeeName) {
								handleManagerOverrideChange(managerId, null);
								return;
							}
							handleManagerOverrideChange(managerId, {
								value: employeeId,
								label: employeeName,
							});
						}}
						fetchEmployees={fetchEmployeesForRow}
						placeholder="Select approver"
						isClearable={false}
						minOptionsForCombobox={8}
					/>
				</div>
			);
		};

		return [
			{
				field: "name",
				headerName: "Manager",
				sortable: true,
				flex: 1,
				sort: sortBy === "name" ? sortOrder : null,
				cellRenderer: ManagerNameCellRenderer,
			},
			{
				headerName: "Acting Manager",
				flex: 1,
				sortable: false,
				cellRenderer: ManagerOverrideCellRenderer,
				cellStyle: {
					paddingRight: "24px",
					display: "flex",
					alignItems: "center",
				},
			},
			{
				field: "directReportCount",
				headerName: "Direct Reports",
				type: "text",
				sortable: true,
				flex: 1.3,
				sort: sortBy === "directReportCount" ? sortOrder : null,
			},
			{
				field: "totalCompensation",
				headerName: "Team Compensation",
				type: "text",
				sortable: true,
				flex: 1.3,
				sort: sortBy === "totalCompensation" ? sortOrder : null,
				valueFormatter: (params) => {
					const value = params.value as number | undefined;
					if (value === undefined || value === null) return "$0.00";
					return `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
				},
			},
		];
	}, [value, sortBy, sortOrder, buildFetchOptions]);

	const handleSortChanged = (event: SortChangedEvent<ManagerRow>) => {
		const columnState = event.api.getColumnState();
		const sortedColumn = columnState.find((col) => col.sort !== null);

		if (sortedColumn && sortedColumn.colId) {
			setSortBy(sortedColumn.colId as "name" | "directReportCount" | "totalCompensation");
			setSortOrder(sortedColumn.sort === "asc" ? "asc" : "desc");
			setCurrentPage(1); // Reset to first page when sorting changes
		}
	};

	const handleSearchChange = (value: string) => {
		setSearchTerm(value);
		setCurrentPage(1); // Reset to first page when search changes
	};

	const totalPages = Math.ceil((data?.totalCount ?? 0) / limit);

	return (
		<div>
			<div className="mb-3">
				<div className="flex items-center justify-between gap-2">
					<div className="w-[300px]">
						<InputText
							id="manager-search"
							placeholder="Search manager..."
							value={searchTerm}
							onChange={handleSearchChange}
							leftIcon={faMagnifyingGlass}
						/>
					</div>
					<Tooltip content="Reset table to default settings" placement="top">
						<ButtonIcon
							icon={faRotateLeft}
							variant="outline"
							color="neutral"
							shade={5}
							size={32}
							aria-label="Reset table to default settings"
							onClick={() => {
								// remove all manager overrides from the form object
								onChange({});
							}}
						/>
					</Tooltip>
				</div>
			</div>
			<div className="flex flex-col gap-4">
				<div className="h-[500px]">
					<Table<ManagerRow>
						rowData={data?.managers ?? []}
						columnDefs={columnDefs}
						loading={isLoading}
						onSortChanged={handleSortChanged}
						getRowId={(params) => params.data._id}
					/>
				</div>
				{totalPages > 1 && (
					<div className="flex justify-end">
						<Pagination pageCount={totalPages} currentPage={currentPage} onPageChange={setCurrentPage} />
					</div>
				)}
			</div>
		</div>
	);
};
